import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { SectionHeading } from "../components/PageBits";
import { Reveal } from "../components/Reveal";

export default function FounderPage() {
  return (
    <>
      {/* 1. Hero */}
      <section className="grain relative overflow-hidden bg-navy pt-40 pb-20 sm:pt-48 sm:pb-28" data-testid="founder-hero">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -left-40 top-10 h-[480px] w-[480px] rounded-full bg-gold/10 blur-[150px]" />
        </div>
        <div className="container-luxe relative z-10 grid grid-cols-1 items-start gap-14 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -inset-3 rounded-sm border border-gold/20" />
              <div className="relative flex aspect-[3/4] w-full items-center justify-center overflow-hidden rounded-sm bg-navy-soft/40">
                <div className="text-center">
                  <span className="font-serif text-7xl text-gold/70">J·G</span>
                  <span className="mt-4 block text-[0.6rem] uppercase tracking-luxe text-cream/50">
                    Fotografía oficial próximamente
                  </span>
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-navy/40 to-transparent" />
              </div>
            </div>
          </Reveal>

          <div className="lg:col-span-7">
            <Reveal>
              <p className="overline mb-6 flex items-center gap-4">
                <span className="h-px w-10 bg-gold" /> Detrás del método
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <h1 className="font-serif text-4xl font-light leading-[1.02] tracking-tight text-cream sm:text-6xl lg:text-7xl">
                José C. <span className="italic text-gold">Guevara</span>
              </h1>
            </Reveal>
            <div className="mt-8 space-y-5">
              {[
                "Antes de construir un método para enseñar a otros, emprendí una búsqueda para comprender mi propia historia.",
                "Durante años estudié distintos caminos de crecimiento personal, espiritualidad y desarrollo humano. Cada uno aportaba respuestas valiosas, pero también dejaba preguntas abiertas.",
                "Con el tiempo comprendí que la transformación profunda no ocurría únicamente cambiando la forma de pensar. Era necesario comprender la historia que habíamos vivido, las emociones que seguían presentes y la manera en que todo eso influía en nuestra forma de vivir.",
              ].map((t, i) => (
                <Reveal key={t} delay={0.2 + i * 0.08}>
                  <p className="text-base font-light leading-relaxed text-cream/75 sm:text-lg">{t}</p>
                </Reveal>
              ))}
              <Reveal delay={0.5}>
                <p className="font-serif text-2xl font-light italic leading-snug text-gold">
                  Esa búsqueda fue el origen de Desde el Primer Latido™.
                </p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* 2. Una búsqueda que cambió de dirección */}
      <section className="relative bg-cream py-24 text-navy sm:py-32" data-testid="founder-origin">
        <div className="container-luxe">
          <SectionHeading dark={false} overline="El origen" title="La respuesta no estaba" accent="en un solo camino." />
          <div className="grid grid-cols-1 gap-14 lg:grid-cols-12">
            <div className="flex flex-col gap-6 lg:col-span-7">
              <Reveal>
                <p className="text-lg font-light leading-relaxed text-navy/80">
                  En ese recorrido descubrí que distintos enfoques ayudaban a comprender diferentes
                  dimensiones del ser humano. Sin embargo, sentía que algo seguía faltando.
                </p>
              </Reveal>
              <Reveal delay={0.1}>
                <p className="text-lg font-light leading-relaxed text-navy/80">
                  Fue esa necesidad de integrar lo aprendido la que dio origen a una metodología donde
                  la comprensión, la conciencia y la transformación forman parte de un mismo proceso.
                </p>
              </Reveal>
            </div>
            <Reveal delay={0.2} className="lg:col-span-5">
              <p className="font-serif text-3xl font-light leading-snug text-navy">
                No nació para crear un curso.
                <br />
                <span className="italic text-gold-deep">
                  Nació para responder una pregunta que primero necesité responder para mí.
                </span>
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* 3. Del aprendizaje al método */}
      <section className="grain relative overflow-hidden bg-navy py-24 sm:py-32" data-testid="founder-birth">
        <div className="container-luxe relative z-10">
          <SectionHeading
            overline="Cómo nació la Academia"
            title="Compartir aquello que primero"
            accent="transformó mi manera de ver la vida."
          />
          <div className="grid grid-cols-1 gap-14 lg:grid-cols-12">
            <div className="flex flex-col gap-6 lg:col-span-7">
              <Reveal>
                <p className="text-lg font-light leading-relaxed text-cream/75">
                  Con el tiempo comprendí que muchas personas recorrían las mismas preguntas en
                  silencio.
                </p>
              </Reveal>
              <Reveal delay={0.1}>
                <p className="text-lg font-light leading-relaxed text-cream/75">
                  Así nació la Academia Desde el Primer Latido™: un espacio para comprender los
                  patrones de pensamiento, las emociones y la conciencia desde una perspectiva
                  integrada, práctica y profundamente humana.
                </p>
              </Reveal>
            </div>
            <Reveal delay={0.2} className="lg:col-span-5">
              <p className="font-serif text-3xl font-light leading-snug text-cream">
                No para ofrecer respuestas rápidas.
                <br />
                <span className="italic text-gold">
                  Sino para acompañar procesos de comprensión que puedan sostenerse en el tiempo.
                </span>
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* 4. Quién soy hoy */}
      <section className="relative bg-cream py-24 text-navy sm:py-32" data-testid="founder-today">
        <div className="container-luxe">
          <SectionHeading dark={false} overline="Hoy" title="Sigo aprendiendo junto a quienes" accent="recorren este camino." />
          <div className="grid grid-cols-1 gap-14 lg:grid-cols-12">
            <Reveal className="lg:col-span-7">
              <p className="text-lg font-light leading-relaxed text-navy/80">
                Continúo investigando, escribiendo y desarrollando nuevos recursos para que la Academia
                siga creciendo.
              </p>
            </Reveal>
            <Reveal delay={0.15} className="lg:col-span-5">
              <p className="font-serif text-3xl font-light leading-snug text-navy">
                Mi compromiso no es ofrecer todas las respuestas.
                <br />
                <span className="italic text-gold-deep">
                  Es seguir haciendo mejores preguntas y compartir las herramientas que ayuden a cada
                  persona a comprenderse con mayor profundidad.
                </span>
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* 5. Cierre */}
      <section className="grain relative overflow-hidden bg-navy-deep py-24 sm:py-32" data-testid="founder-closing">
        <div className="container-luxe relative z-10 max-w-3xl">
          <Reveal>
            <p className="font-serif text-2xl font-light leading-snug text-cream sm:text-3xl">
              Todo método tiene un origen. Esta Academia nació de una búsqueda personal.
              <br />
              <span className="italic text-gold">
                Hoy esa búsqueda continúa en cada programa, cada libro, cada encuentro y cada
                conversación.
              </span>
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <Link
              to="/academy"
              className="group mt-10 inline-flex items-center gap-2 text-base text-gold underline-offset-4 transition-colors hover:underline"
              data-testid="founder-closing-link"
            >
              Conocer la Academia
              <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
          </Reveal>
        </div>
      </section>
    </>
  );
}
